import { useCallback, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { ToastContext } from './toast'
import type { ShowToastOptions, ToastStatus } from './toast'

interface ToastItem {
  id: number
  status: ToastStatus
  content: ReactNode
}

const STATUS_CLASS: Record<ToastStatus, string> = {
  success: 'alert-success',
  error: 'alert-error',
  warning: 'alert-warning',
  info: 'alert-info'
}

const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const nextId = useRef(0)

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const showToast = useCallback(
    ({ content, status = 'info', sticky = false, durationMs = 5000 }: ShowToastOptions) => {
      const id = nextId.current
      nextId.current += 1
      setToasts((prev) => [...prev, { id, status, content }])
      if (!sticky) {
        window.setTimeout(() => dismiss(id), durationMs)
      }
    },
    [dismiss]
  )

  const value = useMemo(() => ({ showToast }), [showToast])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className='toast toast-top toast-end z-50 max-w-md whitespace-normal'>
        {toasts.map((t) => (
          <div
            key={t.id}
            role='alert'
            className={`alert ${STATUS_CLASS[t.status]} shadow-lg`}
          >
            <div className='flex w-full items-start gap-3'>
              <div className='flex-1 text-left text-sm'>{t.content}</div>
              <button
                type='button'
                aria-label='Dismiss notification'
                className='btn btn-ghost btn-xs'
                onClick={() => dismiss(t.id)}
              >
                ✕
              </button>
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export default ToastProvider
